import {DOCUMENT, Injector, NgZone} from '@angular/core';
import {animationFrameScheduler, fromEvent, merge, NEVER, Observable} from 'rxjs';
import {
    auditTime,
    filter,
    finalize,
    map,
    repeat,
    scan,
    skipWhile,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {dependOn} from './rxjs/depend-on';
import {fromEventOutsideAngularZone} from './rxjs/from-event-outside-angular-zone';

export type Point = [number, number];

type X = 0;
type Y = 1;
export const X: X = 0;
export const Y: Y = 1;
export type DragHandlerAxis = X | Y;

export interface PointerPositionInfo {
    pos: Point;
    startPos: Point;
    shift: Point;
}

function subtractPoints(point1: Point, point2: Point): Point {
    return [
        point1[X] - point2[X],
        point1[Y] - point2[Y]
    ];
}

const LEFT_MOUSE_BUTTON = 0;
const KEY_ESCAPE = 27;

const MOUSE_DOWN = 'mousedown';
const TOUCH_START = 'touchstart';
const MOUSE_MOVE = 'mousemove';
const TOUCH_MOVE = 'touchmove';
const MOUSE_UP = 'mouseup';
const TOUCH_END = 'touchend';
const MOUSE_LEAVE = 'mouseleave';
const KEY_DOWN = 'keydown';

interface DragHandlerInitArgs {
    element: HTMLElement;
    destroy$: Observable<any>;
    axis: Set<DragHandlerAxis>;
    movingInitialThreshold: number;
    startMoving: (posInfo: PointerPositionInfo) => void;
    endMoving: () => void;
    apply: (posInfo: PointerPositionInfo) => void;
    cancel: () => void;
    preview: (posInfo: PointerPositionInfo) => void;
    transformPos: (posInfo: PointerPositionInfo) => PointerPositionInfo;
}

interface PointerListeners {
    down: (element: HTMLElement) => Observable<Point>;
    move: (element: HTMLElement) => Observable<Point>;
    up: (element: HTMLElement) => Observable<Point>;
    cancel: (element: HTMLElement) => Observable<null>;
}

interface PointerListenerResult {
    pos: Point;
    listeners: PointerListeners;
}
interface MovingListenerArgs {
    listeners: PointerListeners;
    element: HTMLElement;
    axis: Set<DragHandlerAxis>;
    startPos: Point;
    movingInitialThreshold: number;
    startMoving: (posInfo: PointerPositionInfo) => void;
    preview: (posInfo: PointerPositionInfo) => void;
    transformPos: (posInfo: PointerPositionInfo) => PointerPositionInfo;
}

interface PointerListenerArgs {
    listeners: PointerListeners;
    element: HTMLElement;
}

export class DragHandler {

    private static getMouseEventPos(event: MouseEvent): Point {
        return [event.clientX, event.clientY];
    }

    private static getTouchEventPos(event: TouchEvent): Point {
        const {clientX, clientY} = event.changedTouches[0];

        return [clientX, clientY];
    }

    private static stopMouseEvent(event: MouseEvent): void {
        event.preventDefault();
    }

    private static stopTouchEvent(event: TouchEvent): void {
        event.stopPropagation();
    }

    private static isShiftAboveThreshold(shift: Point, threshold: number, axis?: Set<DragHandlerAxis>): boolean {
        const [x, y] = shift;
        const length = !axis || axis.size === 2
            ? Math.sqrt(x * x + y * y)
            : Math.abs(axis.has(X) ? x : y);

        return length >= threshold;
    }

    private static alignMovingToAxis(posInfo: PointerPositionInfo, axis?: Set<DragHandlerAxis>): void {
        if (axis && axis.size < 2) {
            if (!axis.has(X)) {
                posInfo.shift[X] = 0;
                posInfo.pos[X] = posInfo.startPos[X];
            }
            if (!axis.has(Y)) {
                posInfo.shift[Y] = 0;
                posInfo.pos[Y] = posInfo.startPos[Y];
            }
        }
    }

    private readonly document = this.injector.get(DOCUMENT);
    private readonly ngZone = this.injector.get(NgZone);

    private mouseListeners: PointerListeners = this.getMouseListeners();
    private touchListeners: PointerListeners = this.getTouchListeners();

    constructor(
        private injector: Injector
    ) {}

    public init(args: DragHandlerInitArgs): Observable<any> {
        return this.listenToDown(args.element).pipe(
            take(1),
            switchMap(({pos: startPos, listeners}) => {
                let lastPosInfo: PointerPositionInfo = null;

                const up$ = this.listenToUp({
                    listeners,
                    element: args.element
                }).pipe(
                    tap(() => {
                        if (lastPosInfo && (lastPosInfo.shift[X] !== 0 || lastPosInfo.shift[Y] !== 0)) {
                            args.apply(lastPosInfo);
                        }
                    })
                );
                const cancel$ = this.listenToCancel({
                    listeners,
                    element: args.element
                }).pipe(
                    tap(() => args.cancel())
                );
                const visualize$ = this.listenToMovingVisualization({
                    listeners,
                    element: args.element,
                    axis: args.axis,
                    startPos,
                    movingInitialThreshold: args.movingInitialThreshold,
                    startMoving: args.startMoving,
                    preview: args.preview,
                    transformPos: args.transformPos
                }).pipe(
                    tap(posInfo => lastPosInfo = posInfo)
                );

                return merge(
                    up$,
                    cancel$
                ).pipe(
                    take(1),
                    finalize(() => {
                        if (lastPosInfo) {
                            args.endMoving();
                        }
                    }),
                    dependOn(visualize$)
                );
            }),
            repeat(),
            takeUntil(args.destroy$)
        );
    }

    private listenToDown(element: HTMLElement): Observable<PointerListenerResult> {
        return merge(
            this.mouseListeners.down(element).pipe(
                map(pos => ({
                    pos,
                    listeners: this.mouseListeners
                } as PointerListenerResult))
            ),
            this.touchListeners.down(element).pipe(
                map(pos => ({
                    pos,
                    listeners: this.touchListeners
                } as PointerListenerResult))
            )
        );
    }

    private listenToMovingVisualization(args: MovingListenerArgs): Observable<PointerPositionInfo> {
        return args.listeners.move(args.element).pipe(
            map<Point, PointerPositionInfo>(pos => ({
                pos,
                startPos: args.startPos,
                shift: subtractPoints(pos, args.startPos)
            })),
            skipWhile(posInfo =>
                !DragHandler.isShiftAboveThreshold(posInfo.shift, args.movingInitialThreshold, args.axis)
            ),
            tap(posInfo => DragHandler.alignMovingToAxis(posInfo, args.axis)),
            map(posInfo => args.transformPos(posInfo)),
            scan((prevPosInfo, posInfo) => {
                if (prevPosInfo === null) {
                    args.startMoving(posInfo);
                }

                return posInfo;
            }, null as PointerPositionInfo),
            auditTime(0, animationFrameScheduler),
            tap(posInfo => args.preview(posInfo))
        );
    }

    private listenToUp(args: PointerListenerArgs): Observable<Point> {
        return args.listeners.up(args.element);
    }

    private listenToCancel(args: PointerListenerArgs): Observable<Point> {
        return merge(
            args.listeners.cancel(args.element),
            this.listenToCancelByKeyboard()
        );
    }

    private listenToCancelByKeyboard(): Observable<any> {
        return fromEvent<KeyboardEvent>(this.document, KEY_DOWN).pipe(
            filter(event => event.keyCode === KEY_ESCAPE)
        );
    }

    private getMouseListeners(): PointerListeners {
        return {
            down:   (...args) => this.listenToMouseDown(...args),
            move:   () => this.listenToMouseMove.apply(this),
            up:     () => this.listenToMouseUp.apply(this),
            cancel: () => this.listenToMouseLeave.apply(this)
        };
    }

    private getTouchListeners(): PointerListeners {
        return {
            down:   (...args) => this.listenToTouchStart(...args),
            move:   () => this.listenToTouchMove.apply(this),
            up:     () => this.listenToTouchEnd.apply(this),
            cancel: () => NEVER
        };
    }

    private listenToMouseDown(element: HTMLElement): Observable<Point> {
        return fromEventOutsideAngularZone<MouseEvent>(this.ngZone, element, MOUSE_DOWN).pipe(
            filter(event => event.button === LEFT_MOUSE_BUTTON),
            tap(DragHandler.stopMouseEvent),
            map(DragHandler.getMouseEventPos)
        );
    }

    private listenToTouchStart(element: HTMLElement): Observable<Point> {
        return fromEventOutsideAngularZone<TouchEvent>(this.ngZone, element, TOUCH_START).pipe(
            tap(DragHandler.stopTouchEvent),
            map(DragHandler.getTouchEventPos)
        );
    }

    private listenToMouseMove(): Observable<Point> {
        return fromEventOutsideAngularZone<MouseEvent>(this.ngZone, this.document, MOUSE_MOVE).pipe(
            map(DragHandler.getMouseEventPos)
        );
    }

    private listenToTouchMove(): Observable<Point> {
        return fromEventOutsideAngularZone<TouchEvent>(this.ngZone, this.document, TOUCH_MOVE).pipe(
            tap(DragHandler.stopTouchEvent),
            map(DragHandler.getTouchEventPos)
        );
    }

    private listenToMouseUp(): Observable<Point> {
        return fromEvent<MouseEvent>(this.document, MOUSE_UP).pipe(
            filter(event => event.button === LEFT_MOUSE_BUTTON),
            map(DragHandler.getMouseEventPos)
        );
    }

    private listenToTouchEnd(): Observable<Point> {
        return fromEvent<TouchEvent>(this.document, TOUCH_END).pipe(
            map(DragHandler.getTouchEventPos)
        );
    }

    private listenToMouseLeave(): Observable<null> {
        return fromEvent<MouseEvent>(this.document, MOUSE_LEAVE).pipe(
            map((): null => null)
        );
    }

}
